import { INTERFACES_UI_BASE_URL } from "@zapier/utils";

export type SnippetType = "script" | "iframe";

interface Input {
  allowQueryParameters: boolean;
  frameHeight: string;
  frameWidth: string;
  includeBackground: boolean;
  pageId: string;
  snippetType: SnippetType;
}

function scriptSnippet({
  allowQueryParameters,
  frameHeight,
  frameWidth,
  includeBackground,
  pageId,
}: Input): string {
  const scriptSource = `${INTERFACES_UI_BASE_URL}/assets/web-components/zapier-interfaces/zapier-interfaces.esm.js`;
  const script = `<script type="module" src="${scriptSource}"></script>`;

  const webComponentAttributes = [
    `page-id="${pageId}"`,
    `no-background="${includeBackground ? "false" : "true"}"`,
    allowQueryParameters ? `allow-query-params="true" query-params=""` : "",
    `style="max-width: ${frameWidth}; height: ${frameHeight};"`,
  ]
    .filter(Boolean)
    .join(" ");

  const webComponent = `<zapier-interfaces-page-embed ${webComponentAttributes}></zapier-interfaces-page-embed>`;

  return `${script}\n${webComponent}`;
}

function iframeSnippet({
  allowQueryParameters,
  frameHeight,
  frameWidth,
  includeBackground,
  pageId,
}: Input): string {
  const sourceUrl = new URL(`/embed/page/${pageId}`, INTERFACES_UI_BASE_URL);

  if (!includeBackground) {
    sourceUrl.searchParams.set("noBackground", "true");
  }

  if (allowQueryParameters) {
    sourceUrl.searchParams.set("allowQueryParams", "true");
  }

  return `<iframe src="${sourceUrl.href}" style="max-width: ${frameWidth}; width: 100%; height: ${frameHeight};"></iframe>`;
}

const snippetBuilders: Record<SnippetType, (input: Input) => string> = {
  iframe: iframeSnippet,
  script: scriptSnippet,
};

/**
 * Generates the embed code snippet for a Zapier Interface page.
 * @param options - Options for snippet generation
 * @returns The HTML snippet as a string
 */
export function buildEmbedSnippet(input: Input): string {
  const snippetBuilder = snippetBuilders[input.snippetType];
  return snippetBuilder(input);
}
